'use client';

import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ChartData
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import styles from '../ShareholdingCharts.module.css';
import { LABELMAP } from '../label-constants';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Define the data point structure
interface ShareholdingDataPoint {
    date: Date;
    [key: string]: number | Date | string; // Dynamic fields for shareholding percentages
}

// Define component props
interface BarChartProps {
    data: ShareholdingDataPoint[];
    series: string[];
    title?: string;
    description?: string;
}

export function BarChart({ data, series, title, description }: BarChartProps): React.ReactElement {
    // Get time periods (for x-axis) - ONLY YEAR
    const labels: string[] = data.map(item => {
        const date = new Date(item.date);
        return date.getFullYear().toString(); // Only show the year
    });

    // Chart colors
    const backgroundColors: string[] = [
        'rgba(59, 130, 246, 0.8)',  // Blue
        'rgba(16, 185, 129, 0.8)',  // Green
    ];

    const borderColors: string[] = [
        'rgba(59, 131, 246, 0)',  // Blue
        'rgba(16, 185, 129, 0)',  // Green
    ];

    // Get label from LABELMAP or format the key
    const getLabel = (key: string): string => {
        return LABELMAP[key] || key
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const chartData: ChartData<'bar'> = {
        labels: Array.from(new Set(labels)), // Remove duplicate years
        datasets: series.map((key, index) => ({
            label: getLabel(key),
            data: data.map(d => Number(d[key]) || 0),
            backgroundColor: backgroundColors[index % backgroundColors.length],
            borderColor: borderColors[index % borderColors.length],
            borderWidth: 1,
            borderRadius: {
                topLeft: 0,
                topRight: 0,
                bottomLeft: 0,
                bottomRight: 0,
            },
            barPercentage: 0.9,
            categoryPercentage: 0.9,
            stack: 'stack1', // Important: This makes the bars stack
        })),
    };

    const options: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {
                    usePointStyle: true,
                    boxWidth: 8,
                    boxHeight: 8,
                    padding: 15,
                    font: {
                        size: 11,
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => `${context.dataset.label}: ${(context.raw as number).toFixed(2)}%`
                }
            }
        },
        scales: {
            x: {
                stacked: true, // Enable stacking on x-axis
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    maxTicksLimit: 7,
                    maxRotation: 0,
                    font: {
                        size: 10,
                    }
                },
            },
            y: {
                stacked: true, // Enable stacking on y-axis
                beginAtZero: true,
                border: {
                    display: false,
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                },
                max: 100, // Set max to 100% since these are percentages
                ticks: {
                    callback: value => `${value}%`,
                    maxTicksLimit: 5,
                    maxRotation: 0,
                    font: {
                        size: 10,
                    }
                }
            },
        },
    };

    return (
        <div className={styles.chartWrapper}>
            <Bar data={chartData} options={options} />
        </div>
    );
}